import { memo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Send, Eye, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "@/components/confirm-dialog/ConfirmDialog";
import { CONTRACTOR_CARD_FILE_INPUT_ID } from "@/constants";
import { HiddenFileInput } from "@/components/HiddenFileInput";
import { triggerFileInputClick } from "@/lib/fileManagerUtils";
import Spinner from "@/components/Spinner";

interface ContractorCardFileUploadActionsProps {
  hasFile: boolean;
  hasPendingFile: boolean;
  isUploading: boolean;
  canUpdate: boolean;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onConfirmSend: () => void;
  onViewFile?: () => void;
  onCancelPending?: () => void;
  className?: string;
}

export const ContractorCardFileUploadActions = memo(
  ({
    hasFile,
    hasPendingFile,
    isUploading,
    canUpdate,
    onFileChange,
    onConfirmSend,
    onViewFile,
    onCancelPending,
    className = "mt-4 space-y-2",
  }: ContractorCardFileUploadActionsProps) => {
    const { t } = useTranslation();

    const handleUploadClick = useCallback(() => {
      triggerFileInputClick(CONTRACTOR_CARD_FILE_INPUT_ID);
    }, []);

    return (
      <div className={className}>
        <HiddenFileInput
          id={CONTRACTOR_CARD_FILE_INPUT_ID}
          onChange={onFileChange}
          disabled={isUploading}
        />

        {hasFile && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={onViewFile}
              disabled={isUploading}
              className="flex-1"
            >
              <Eye className="w-4 h-4 mr-2" />
              {t("contractorCards.actions.view")}
            </Button>
            {hasPendingFile && (
              <Button
                variant="outline"
                onClick={onCancelPending}
                disabled={isUploading}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                {t("contractorCards.actions.cancel")}
              </Button>
            )}
          </div>
        )}

        {canUpdate && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleUploadClick}
              disabled={isUploading}
              className="flex-1"
            >
              <Upload className="w-4 h-4 mr-2" />
              {hasFile
                ? t("contractorCards.actions.replace")
                : t("contractorCards.actions.upload")}
            </Button>

            {hasPendingFile && (
              <ConfirmDialog
                title={t("contractorCards.messages.confirmUpload")}
                content={t("contractorCards.messages.confirmUploadMessage")}
                yesLabel={t("contractorCards.actions.confirm")}
                noLabel={t("contractorCards.actions.cancel")}
                onConfirm={onConfirmSend}
              >
                <Button
                  variant="default"
                  disabled={isUploading}
                  className="flex-1"
                >
                  {isUploading ? (
                    <Spinner size="sm" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {t("contractorCards.actions.send")}
                </Button>
              </ConfirmDialog>
            )}
          </div>
        )}
      </div>
    );
  },
);
